import { useEffect, useState } from 'react'
import { AddressInfo, customerData, CustomerReceiptData, CustomerShippoReceiptData, ShippoRate } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { getShippoAddress, refreshAccessToken } from '../../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import CreditCardInput from 'react-credit-card-input'
import { PaymentConfirmation, PaymentDeclined } from '../Payment/PaymentModals'

export function ShippoLabel() {
  const userAddress = new AddressInfo(new AddressInfo())

  const navigate = useNavigate()

  const MAX_FAILED_ATTEMPTS = 3
  const LOCKOUT_TIME_MS = 60 * 60 * 1000

  const [selectedCarrier, setSelectedCarrier] = useState('')
  const [shippoStep, setShippoStep] = useState(0)
  const [fetchingShippoRates, setFetchingShippoRates] = useState(false)
  const [shippoRates, setShippoRates] = useState([])
  const [selectedShippoRate, setSelectedShippoRate] = useState(new ShippoRate())
  const [errors, setErrors] = useState({})

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })

  const [payingRate, setPayingRate] = useState(false)
  const [paymentDeclined, setPaymentDeclined] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentCode, setPaymentCode] = useState('')
  const [retriesRemaining, setRetriesRemaining] = useState(3)

  const [confirmPayment, setConfirmPayment] = useState(false)
  const [generatedReceipt, setGeneratedReceipt] = useState(new CustomerReceiptData())

  const handleCardNumberChange = (e) => {
    setFormData({ ...formData, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setFormData({ ...formData, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setFormData({ ...formData, cvv: e.target.value })
  }

  const handlePayShippoRate = async () => {
    setPayingRate(true)

    const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setPaymentDeclined(true)
        setPayingRate(false)
        return
      } else {
        localStorage.removeItem('failedAttempts')
        localStorage.removeItem('lastFailedAttempt')
      }
    }

    const form = new FormData()
    form.append('shippoRate', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      labelType: 'Advance Funding',
      customer: customerData.customerLeadData.leadContact,
      customerCreditCardInfo: {
        cardNumber: formData.cardNumber,
        cardExpiryDate: formData.expiryDate,
        cardCVV: formData.cvv
      },
      shippoRate: selectedShippoRate
    }))

    const payShippoRateResponse = await fetch(`${apiURL}/api/payShippoRate`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const payShippoRateData = await payShippoRateResponse.json()
    setPayingRate(false)
    if (payShippoRateData.response.error) {
      if (payShippoRateData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handlePayShippoRate() : navigate('/', { state: { expired: true } })
      } else
        console.error(payShippoRateData.response.error)
    } else {
      if (payShippoRateData.response.paymentError) {
        const currentTime = new Date().getTime()
        let failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
        let lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

        failedAttempts++
        lastFailedAttempt = currentTime

        localStorage.setItem('failedAttempts', failedAttempts)
        localStorage.setItem('lastFailedAttempt', lastFailedAttempt)

        if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
          setPaymentError('You have reached the maximum number of failed payment attempts. Please try again in 60 minutes.')
          setRetriesRemaining(0)
          setPaymentCode('')
          setPaymentDeclined(true)
        } else {
          setFormData({
            cardNumber: '',
            expiryDate: '',
            cvv: ''
          })
          setPaymentError(payShippoRateData.response.paymentError)
          setPaymentCode(payShippoRateData.response.paymentErrorCode)
          setRetriesRemaining(3 - failedAttempts)
          setPaymentDeclined(true)
        }
      } else {
        handleResetCustomerDocs()
        customerData.customerShippoReceipts.push(new CustomerShippoReceiptData(payShippoRateData.response.shippoReceipt))
        setGeneratedReceipt(new CustomerShippoReceiptData(payShippoRateData.response.shippoReceipt))
        setConfirmPayment(true)
      }
    }
  }

  const handlePaymentConfirmation = () => {
    customerData.updateCustomerStage('38513a26-c374-48f4-ab3b-808059c6e221', '04144458-31da-4f86-be7b-69051d6c1cc9', 'AF - Documents Available')
    navigate('/dashboard')
  }

  const handleResetCustomerDocs = async () => {
    const resetResponse = await fetch(`${apiURL}/api/resetAFCustomerDocs?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}&contactId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const resetData = await resetResponse.json()
    if(resetData.response.error) {
      if(resetData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleResetCustomerDocs() : navigate('/', { state: { expired: true } })
      } else
        console.error(resetData.response.error)
    }
  }

  const handleGetShippoRates = async () => {
    setFetchingShippoRates(true)

    const form = new FormData()
    form.append('shippoInfo', JSON.stringify({
      fromAddress: userAddress,
      toAddress: getShippoAddress('Advance Funding', userAddress.state, selectedCarrier),
      toAddressName: 'SETC Pros Inc.',
      leadContact: customerData.customerLeadData.leadContact,
      refundType: 'Advance Funding',
      specificCarrier: selectedCarrier
    }))

    const shippoRatesResponse = await fetch(`${apiURL}/api/getShippoRates`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const shippoRatesData = await shippoRatesResponse.json()
    setFetchingShippoRates(false)
    if (shippoRatesData.response.error) {
      if (shippoRatesData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleGetShippoRates() : navigate('/', { state: { expired: true } })
      } else
        console.error(shippoRatesData.response.error)
    } else
      setShippoRates(shippoRatesData.response.shippoRates)
  }

  useEffect(() => {
    handleGetShippoRates()
  }, [])

  return (
    <div className='w-full flex flex-col items-center gap-4 mb-10'>
      {!fetchingShippoRates ? (
        shippoStep === 0 ? (
          <div className='w-3/4 h-full flex flex-col items-center mb-10'>
            <p className='font-bold text-xl'>Available Rates</p>
            <p>Please view below the available rates you can choose from.</p>
            <div className='w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4'>
              {shippoRates.map((_shippoRate, index) => {
                const shippoRate = new ShippoRate(_shippoRate)
                return (
                  <div onClick={() => setSelectedShippoRate(shippoRate)} key={index} className={`cursor-pointer border rounded-lg p-4 shadow-md flex flex-col items-center transition ${selectedShippoRate.objectId === shippoRate.objectId ? 'bg-primaryColor' : 'bg-gray-100 hover:bg-gray-200'}`}>
                    {shippoRate.providerImage75 && (
                      <img src={shippoRate.providerImage200} alt={`${shippoRate.provider} logo`} className='w-12 h-12 mb-4' />
                    )}
                    <p className='text-lg font-bold'>{shippoRate.provider}</p>
                    {Number(shippoRate.amount) > 0 && (
                      <p className='text-gray-700'>
                        {shippoRate.currency} {shippoRate.amount}
                      </p>
                    )}
                    <p className='text-sm text-gray-600 mt-2'>
                      {shippoRate.estimatedDays ? `${shippoRate.estimatedDays} estimated days` : 'No estimate available'}
                    </p>
                    <p className='text-sm text-gray-500'>{shippoRate.durationTerms}</p>
                  </div>
                )
              })}
            </div>
            <div className='flex w-full justify-center'>
              <button
                disabled={!selectedShippoRate.objectId}
                onClick={() => setShippoStep(shippoStep + 1)}
                className={`px-8 py-2 mt-6 text-white rounded  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 ${selectedShippoRate.objectId ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
                Next
              </button>
            </div>
          </div>
        ) : (
          <div className='w-1/2 h-fit flex flex-col mb-10 justify-center items-center gap-4 font-mont'>
            <div className='border p-6 rounded-md bg-gray-50 w-full'>
              <p className='text-lg font-semibold mb-2'>Selected Rate:</p>
              <div className='w-full border rounded-lg p-4 shadow-md flex flex-col items-center bg-gray-100'>
                {selectedShippoRate.providerImage200 && (
                  <img src={selectedShippoRate.providerImage200} alt={`${selectedShippoRate.provider} logo`} className='w-12 h-12 mb-4' />
                )}
                <p className='text-lg font-bold'>{selectedShippoRate.provider}</p>
                {Number(selectedShippoRate.amount) > 0 && (
                  <p className='text-gray-700'>
                    {selectedShippoRate.currency} {Number(selectedShippoRate.amount).toFixed(2)}
                  </p>
                )}
                <p className='text-sm text-gray-600 mt-2'>
                  {selectedShippoRate.estimatedDays ? `${selectedShippoRate.estimatedDays} estimated days` : 'No estimate available'}
                </p>
                <p className='text-sm text-gray-500'>{selectedShippoRate.durationTerms}</p>
              </div>
            </div>
            {Number(selectedShippoRate.amount) > 0 ? (
              <div className='flex flex-col'>
                <div className='mb-4 flex flex-col items-center'>
                  <label htmlFor='cardNumber' className='text-sm font-medium text-gray-700 text-start w-full'>Card Number</label>
                  <CreditCardInput
                    cardNumberInputProps={{
                      value: formData.cardNumber,
                      onChange: handleCardNumberChange,
                    }}
                    cardExpiryInputProps={{
                      value: formData.expiryDate,
                      onChange: handleCardExpiryChange,
                    }}
                    cardCVCInputProps={{
                      value: formData.cvv,
                      onChange: handleCardCVCChange,
                    }}
                    fieldClassName='border border-gray-300 rounded-md'
                  />
                  <div className='flex gap-4'>
                    {errors.cardNumber && <p className='text-red-500 text-xs mt-1'>{errors.cardNumber}</p>}
                    {errors.expiryDate && <p className='text-red-500 text-xs mt-1'>{errors.expiryDate}</p>}
                    {errors.cvv && <p className='text-red-500 text-xs mt-1'>{errors.cvv}</p>}
                  </div>
                </div>
                <div className='mb-4'>
                  <button
                    type='submit'
                    onClick={handlePayShippoRate}
                    className={`w-full text-white p-2 rounded-md mt-2 ${payingRate ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                    disabled={payingRate}
                  >
                    {payingRate ? 'Processing...' : 'Submit Payment'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type='submit'
                onClick={handlePayShippoRate}
                className={`lg:w-1/3 text-white p-2 rounded-md ${payingRate ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                disabled={payingRate}
              >
                {payingRate ? 'Generating Label...' : 'Generate Label'}
              </button>
            )}
          </div>
        )
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
      <PaymentDeclined
        isOpen={paymentDeclined}
        setIsOpen={setPaymentDeclined}
        paymentError={paymentError}
        paymentCode={paymentCode}
        retriesRemaining={retriesRemaining}
      />
      <PaymentConfirmation
        isOpen={confirmPayment}
        onClose={handlePaymentConfirmation}
        _customerReceipt={generatedReceipt}
        isShippo={true}
      />
    </div>
  )
}

/***
 * <div onClick={() => setSelectedCarrier('UPS')} className={`w-fit border rounded-lg p-12 shadow-md flex flex-col items-center cursor-pointer ${selectedCarrier === 'UPS' ? 'bg-setcg' : 'bg-gray-100 hover:bg-gray-200'}`}>
                <img src='https://shippo-static.s3.amazonaws.com/providers/200/UPS.png' alt={`${selectedShippoRate.provider} logo`} className='w-16 h-16 mb-4' />
                <p className='text-lg font-bold'>UPS</p>
              </div>
 */